import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Separator } from '../ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import CSSHelperModal from './CSSHelperModal';
import {
  Palette,
  Upload,
  Code,
  Image,
  Loader2,
  Check,
  X,
  Info,
  ExternalLink,
  HelpCircle,
  Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';
import { resolveUploadAssetUrl } from '../../lib/resolveUploadAssetUrl';
import { API_URL } from '../../config';

/** Upload slot → the publish_settings field it writes. Mirrors BRANDING_ASSET_FIELDS on the server. */
const ASSET_FIELDS = {
  logo: 'logo_url',
  favicon: 'favicon_url',
  logo_dark: 'logo_dark_url',
  favicon_dark: 'favicon_dark_url',
};

const ASSET_LABELS = {
  logo: 'Logo',
  favicon: 'Favicon',
  logo_dark: 'Dark logo',
  favicon_dark: 'Dark favicon',
};

/** `logo_dark` → `logo-dark`; keeps the pre-existing `logo` / `favicon` test ids unchanged. */
const testIdFor = (type) => type.replace(/_/g, '-');

/**
 * One upload slot (preview + upload + clear).
 *
 * Dark variants preview on a dark plate — a dark-mode logo is usually light-coloured
 * and would be invisible against the light preview background.
 */
const BrandingAssetSlot = ({ type, kind, value, uploading, onUpload, onClear }) => {
  const inputRef = useRef(null);
  const isDark = type.endsWith('_dark');
  const testId = testIdFor(type);
  const box = kind === 'logo' ? 'w-48 h-16' : 'w-16 h-16';
  const accept = kind === 'logo' ? '.png,.jpg,.jpeg,.gif,.svg,.webp' : '.png,.jpg,.jpeg,.ico,.svg';
  const plate = isDark
    ? 'bg-slate-900 border-slate-700'
    : 'bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700';

  return (
    <div className="flex items-start gap-4">
      {value ? (
        <div className="relative group">
          <div className={`${box} border rounded-lg overflow-hidden flex items-center justify-center p-2 ${plate}`}>
            <img
              src={resolveUploadAssetUrl(value)}
              alt={`${ASSET_LABELS[type]} preview`}
              className="max-w-full max-h-full object-contain"
              data-testid={`${testId}-preview`}
            />
          </div>
          <button
            onClick={() => onClear(type)}
            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
            data-testid={`remove-${testId}-button`}
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      ) : (
        <div
          className={`${box} border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-lg flex items-center justify-center cursor-pointer hover:border-purple-400 transition-colors`}
          onClick={() => inputRef.current?.click()}
        >
          <span className={`${kind === 'logo' ? 'text-sm' : 'text-xs'} text-slate-500 text-center px-1`}>
            {kind === 'logo' ? 'None uploaded' : 'None'}
          </span>
        </div>
      )}

      <div>
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          className="hidden"
          onChange={(e) => onUpload(e.target.files?.[0], type)}
          data-testid={`${testId}-file-input`}
        />
        <Button
          variant="outline"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          data-testid={`upload-${testId}-button`}
        >
          {uploading ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Upload className="w-4 h-4 mr-2" />
          )}
          {value ? 'Change' : 'Upload'}
        </Button>
      </div>
    </div>
  );
};

const BrandingSettings = ({ spaceId, spaceName, orgId, hasCustomCssJs = true }) => {
  const [settings, setSettings] = useState({
    logo_url: '',
    favicon_url: '',
    logo_dark_url: '',
    favicon_dark_url: '',
    primary_color: '#2563eb',
    secondary_color: '#64748b',
    custom_css: '',
    custom_js: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingType, setUploadingType] = useState(null);
  const [showCSSHelper, setShowCSSHelper] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, [spaceId, orgId]);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/organizations/${orgId}/spaces/${spaceId}/publish`);
      setSettings({
        logo_url: response.data.logo_url || '',
        favicon_url: response.data.favicon_url || '',
        logo_dark_url: response.data.logo_dark_url || '',
        favicon_dark_url: response.data.favicon_dark_url || '',
        primary_color: response.data.primary_color || '#2563eb',
        secondary_color: response.data.secondary_color || '#64748b',
        custom_css: response.data.custom_css || '',
        custom_js: response.data.custom_js || '',
      });
    } catch (error) {
      console.error('Failed to fetch branding settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/organizations/${orgId}/spaces/${spaceId}/publish`, {
        primary_color: settings.primary_color,
        secondary_color: settings.secondary_color,
        custom_css: settings.custom_css,
        custom_js: settings.custom_js,
      });
      toast.success('Branding settings saved');
    } catch (error) {
      toast.error('Failed to save branding settings');
    } finally {
      setSaving(false);
    }
  };

  const handleFileUpload = async (file, type) => {
    if (!file) return;

    setUploadingType(type);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('asset_type', type);

    try {
      const response = await axios.post(
        `${API_URL}/organizations/${orgId}/spaces/${spaceId}/branding/upload?asset_type=${type}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      setSettings(prev => ({ ...prev, [ASSET_FIELDS[type]]: response.data.url }));
      toast.success(`${ASSET_LABELS[type]} uploaded successfully`);
    } catch (error) {
      const message = error.response?.data?.detail || `Failed to upload ${ASSET_LABELS[type].toLowerCase()}`;
      toast.error(message);
    } finally {
      setUploadingType(null);
    }
  };

  const clearAsset = async (type) => {
    try {
      await axios.put(`${API_URL}/organizations/${orgId}/spaces/${spaceId}/publish`, {
        [ASSET_FIELDS[type]]: null
      });
      setSettings(prev => ({ ...prev, [ASSET_FIELDS[type]]: '' }));
      toast.success(`${ASSET_LABELS[type]} removed`);
    } catch (error) {
      toast.error('Failed to remove asset');
    }
  };

  if (loading) {
    return (
      <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
        <CardContent className="py-8 text-center">
          <Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800" data-testid="branding-settings">
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-purple-50 dark:bg-purple-900/20">
            <Palette className="w-5 h-5 text-purple-600 dark:text-purple-400" />
          </div>
          <div>
            <CardTitle className="font-[Manrope] text-slate-900 dark:text-white">
              Branding Settings
            </CardTitle>
            <CardDescription>
              Customize the appearance of your public documentation for "{spaceName}"
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="assets" className="space-y-6">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="assets" className="flex items-center gap-2">
              <Image className="w-4 h-4" />
              Logo & Favicon
            </TabsTrigger>
            <TabsTrigger value="colors" className="flex items-center gap-2">
              <Palette className="w-4 h-4" />
              Colors
            </TabsTrigger>
            <TabsTrigger
              value="custom"
              disabled={!hasCustomCssJs}
              className="flex items-center gap-2 data-[disabled]:opacity-60"
              title={
                !hasCustomCssJs
                  ? 'Upgrade to Growth for custom CSS & JavaScript'
                  : undefined
              }
            >
              <Code className="w-4 h-4" />
              Custom Code
              {!hasCustomCssJs && (
                <Sparkles className="w-3.5 h-3.5 text-amber-500 shrink-0 ml-0.5" aria-hidden />
              )}
            </TabsTrigger>
          </TabsList>

          {/* Logo & Favicon Tab */}
          <TabsContent value="assets" className="space-y-6">
            <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 flex items-start gap-2">
              <Info className="w-4 h-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-blue-800 dark:text-blue-200">
                The dark variants are optional. When a reader switches your docs to dark mode,
                the dark asset is used if you uploaded one — otherwise the light asset is kept.
              </p>
            </div>

            {/* Logo Upload — light + dark */}
            <div className="space-y-4">
              <div>
                <Label className="text-base font-semibold">Logo</Label>
                <p className="text-sm text-slate-500 mt-1">
                  Appears in the header of your public documentation.
                  Recommended size: 200x50px (PNG, SVG, or WebP)
                </p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label className="text-sm text-slate-600 dark:text-slate-400">Light mode</Label>
                  <BrandingAssetSlot
                    type="logo"
                    kind="logo"
                    value={settings.logo_url}
                    uploading={uploadingType === 'logo'}
                    onUpload={handleFileUpload}
                    onClear={clearAsset}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm text-slate-600 dark:text-slate-400">Dark mode</Label>
                  <BrandingAssetSlot
                    type="logo_dark"
                    kind="logo"
                    value={settings.logo_dark_url}
                    uploading={uploadingType === 'logo_dark'}
                    onUpload={handleFileUpload}
                    onClear={clearAsset}
                  />
                </div>
              </div>
            </div>

            <Separator />

            {/* Favicon Upload — light + dark */}
            <div className="space-y-4">
              <div>
                <Label className="text-base font-semibold">Favicon</Label>
                <p className="text-sm text-slate-500 mt-1">
                  The small icon that appears in browser tabs.
                  Recommended: 32x32px or 64x64px (PNG, ICO, or SVG)
                </p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label className="text-sm text-slate-600 dark:text-slate-400">Light mode</Label>
                  <BrandingAssetSlot
                    type="favicon"
                    kind="favicon"
                    value={settings.favicon_url}
                    uploading={uploadingType === 'favicon'}
                    onUpload={handleFileUpload}
                    onClear={clearAsset}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm text-slate-600 dark:text-slate-400">Dark mode</Label>
                  <BrandingAssetSlot
                    type="favicon_dark"
                    kind="favicon"
                    value={settings.favicon_dark_url}
                    uploading={uploadingType === 'favicon_dark'}
                    onUpload={handleFileUpload}
                    onClear={clearAsset}
                  />
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Colors Tab */}
          <TabsContent value="colors" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Primary Color */}
              <div className="space-y-3">
                <div>
                  <Label className="text-base font-semibold">Primary Color</Label>
                  <p className="text-sm text-slate-500 mt-1">
                    Used for buttons, links, and accent elements
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <div 
                    className="w-12 h-12 rounded-lg border border-slate-200 dark:border-slate-700 cursor-pointer overflow-hidden"
                    style={{ backgroundColor: settings.primary_color }}
                  >
                    <input
                      type="color"
                      value={settings.primary_color}
                      onChange={(e) => setSettings(prev => ({ ...prev, primary_color: e.target.value }))}
                      className="w-full h-full cursor-pointer opacity-0"
                      data-testid="primary-color-input"
                    />
                  </div>
                  <Input
                    value={settings.primary_color}
                    onChange={(e) => setSettings(prev => ({ ...prev, primary_color: e.target.value }))}
                    placeholder="#2563eb"
                    className="w-32 font-mono text-sm"
                    data-testid="primary-color-text"
                  />
                </div>
              </div>

              {/* Secondary Color */}
              <div className="space-y-3">
                <div>
                  <Label className="text-base font-semibold">Secondary Color</Label>
                  <p className="text-sm text-slate-500 mt-1">
                    Used for borders, backgrounds, and secondary text
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <div 
                    className="w-12 h-12 rounded-lg border border-slate-200 dark:border-slate-700 cursor-pointer overflow-hidden"
                    style={{ backgroundColor: settings.secondary_color }}
                  >
                    <input
                      type="color"
                      value={settings.secondary_color}
                      onChange={(e) => setSettings(prev => ({ ...prev, secondary_color: e.target.value }))}
                      className="w-full h-full cursor-pointer opacity-0"
                      data-testid="secondary-color-input"
                    />
                  </div>
                  <Input
                    value={settings.secondary_color}
                    onChange={(e) => setSettings(prev => ({ ...prev, secondary_color: e.target.value }))}
                    placeholder="#64748b"
                    className="w-32 font-mono text-sm"
                    data-testid="secondary-color-text"
                  />
                </div>
              </div>
            </div>

            {/* Color Preview */}
            <div className="mt-6 p-6 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
              <h4 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-4">Preview</h4>
              <div className="space-y-3">
                <button 
                  className="px-4 py-2 rounded-lg text-white text-sm font-medium"
                  style={{ backgroundColor: settings.primary_color }}
                >
                  Primary Button
                </button>
                <button 
                  className="px-4 py-2 rounded-lg text-white text-sm font-medium ml-2"
                  style={{ backgroundColor: settings.secondary_color }}
                >
                  Secondary Button
                </button>
                <p className="text-sm" style={{ color: settings.primary_color }}>
                  Primary colored link text
                </p>
                <div 
                  className="p-3 rounded-lg text-sm"
                  style={{ 
                    backgroundColor: `${settings.primary_color}15`,
                    borderColor: settings.primary_color,
                    borderWidth: '1px',
                    color: settings.primary_color
                  }}
                >
                  Callout/highlight box with primary color
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Custom Code Tab */}
          <TabsContent value="custom" className="space-y-6">
            {/* Custom CSS */}
            <div className="space-y-3">
              <div className="flex items-start justify-between">
                <div>
                  <Label className="text-base font-semibold">Custom CSS</Label>
                  <p className="text-sm text-slate-500 mt-1">
                    Add custom styles to override the default appearance
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowCSSHelper(true)}
                  className="gap-2"
                  data-testid="open-css-helper-button"
                >
                  <HelpCircle className="w-4 h-4" />
                  CSS Class Reference
                </Button>
              </div>
              <Textarea
                value={settings.custom_css}
                onChange={(e) => setSettings(prev => ({ ...prev, custom_css: e.target.value }))}
                placeholder={`/* Example: Override header background */
[data-testid="public-docs-header"] {
  background-color: #1a1a2e;
}

/* Example: Custom link color */
[data-testid="public-docs-doc-body"] a {
  color: #e94560;
}`}
                className="font-mono text-sm min-h-[200px] bg-slate-950 text-slate-100 dark:bg-slate-950"
                data-testid="custom-css-input"
              />
            </div>

            <Separator />

            {/* Custom JavaScript */}
            <div className="space-y-3">
              <div className="flex items-start justify-between">
                <div>
                  <Label className="text-base font-semibold">Custom JavaScript</Label>
                  <p className="text-sm text-slate-500 mt-1">
                    Add chat widgets (Intercom, Crisp), analytics, or other scripts
                  </p>
                </div>
              </div>
              
              <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 flex items-start gap-2">
                <Info className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-amber-800 dark:text-amber-200">
                  Scripts are injected at the end of the page body. Only add trusted code from verified sources.
                </p>
              </div>
              
              <Textarea
                value={settings.custom_js}
                onChange={(e) => setSettings(prev => ({ ...prev, custom_js: e.target.value }))}
                placeholder={`// Example: Intercom widget
window.intercomSettings = {
  api_base: "https://api-iam.intercom.io",
  app_id: "YOUR_APP_ID"
};

// Example: Crisp chat
window.$crisp=[];
window.CRISP_WEBSITE_ID="YOUR_WEBSITE_ID";`}
                className="font-mono text-sm min-h-[200px] bg-slate-950 text-slate-100 dark:bg-slate-950"
                data-testid="custom-js-input"
              />
            </div>
          </TabsContent>
        </Tabs>

        <Separator className="my-6" />

        <div className="flex justify-end">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-purple-600 hover:bg-purple-700 text-white"
            data-testid="save-branding-button"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Check className="w-4 h-4 mr-2" />
                Save Branding Settings
              </>
            )}
          </Button>
        </div>
      </CardContent>

      {/* CSS Helper Modal */}
      <CSSHelperModal isOpen={showCSSHelper} onClose={() => setShowCSSHelper(false)} />
    </Card>
  );
};

export default BrandingSettings;
